/**
 * The time and signature cards of one mealtime step (PROD-1279 B2, image-18):
 * one card per pack, as cards in the main surface under the table, never a
 * fixed bottom drawer.
 *
 *   Kitchen   the chef's signature on the record and its timestamp. Read-only
 *             facts from the document; the kitchen has no school-side time
 *             reading (plan A2).
 *   Delivery  `Received by` + the delivery `Time reading`, covering every truck
 *             the step receives.
 *   Service   the school's signature + the service `Time reading`; once the
 *             record is signed, who signed it and when.
 *
 * Presentation only. The time inputs are native `type="time"` (a picker, not
 * a numeric box, so the keypad rule does not apply). No `<form>`: Enter in a
 * box must never sign a USDA record (ruling E1).
 */

import { Input } from '@ordo/ui/input'
import type { ReactNode } from 'react'

import { formatTipStamp } from '@/components/cafeteria/cafeteria-mode-tip'
import { formatTaskDate } from '@/lib/production-records/day-tasks'

/** `Signed by Chef Marco · Sep 16, 6:40 AM`, or the name alone without a stamp. Pure. */
export function signedByText(name: string, signedAt: string | null): string {
  return signedAt ? `Signed by ${name} · ${formatTipStamp(signedAt)}` : `Signed by ${name}`
}

/**
 * The Delivery card's helper: which trucks the time covers. `Covers 1 delivery:
 * Wed, Sep 16` / `Covers 2 deliveries: Tue, Sep 15 and Wed, Sep 16` /
 * `No delivery to sign for.` Pure.
 */
export function deliveryCoverageText(deliveryDates: readonly string[]): string {
  const dates = [...new Set(deliveryDates)].sort()
  if (dates.length === 0) {
    return 'No delivery to sign for.'
  }
  const labels = dates.map(formatTaskDate)
  const list = labels.length === 1 ? labels[0]! : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`
  return `Covers ${dates.length} deliver${dates.length === 1 ? 'y' : 'ies'}: ${list}`
}

/**
 * One pack's card. The title sits a full step above the field labels
 * (`text-base` semibold over `text-sm` medium) so the card reads as a section
 * and its labels read as labels; `p-5` matches the write-ins card above it.
 */
function StepCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    // `data-step-card` is what the phone review's "Fix" scrolls to (PROD-1511).
    <section data-step-card={title.toLowerCase()} className="rounded-[10px] border bg-card p-5">
      <h3 className="text-base font-semibold leading-tight">{title}</h3>
      <div className="mt-4 flex flex-col gap-4">{children}</div>
    </section>
  )
}

function Field({ label, children, helper }: { label: string; children: ReactNode; helper?: string }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-sm font-medium">{label}</span>
      {children}
      {helper ? <span className="text-xs leading-relaxed text-muted-foreground">{helper}</span> : null}
    </label>
  )
}

/**
 * `signedByText` rendered two-tone: the NAME carries the weight because it is
 * the fact someone scans for, the stamp is its context. Same characters as the
 * helper, so what a test reads off `textContent` is what `signedByText` says.
 */
function SignedByLine({ name, signedAt }: { name: string; signedAt: string | null }) {
  return (
    <p className="text-sm text-muted-foreground">
      Signed by <span className="font-medium text-foreground">{name}</span>
      {signedAt ? ` · ${formatTipStamp(signedAt)}` : null}
    </p>
  )
}

export function KitchenCard({ signatureName, signedAt }: { signatureName: string | null; signedAt: string | null }) {
  return (
    <StepCard title="Kitchen">
      {signatureName ? (
        <SignedByLine name={signatureName} signedAt={signedAt} />
      ) : (
        <p className="text-sm text-muted-foreground">No kitchen signature yet.</p>
      )}
    </StepCard>
  )
}

export interface DeliveryCardProps {
  /** The step has trucks to sign for: `Received by` renders. */
  hasTrucks: boolean
  receiverName: string
  onReceiverNameChange: (next: string) => void
  time: string
  onTimeChange: (next: string) => void
  /** The trucks' delivery dates, for the coverage line. */
  deliveryDates: readonly string[]
  disabled?: boolean
}

export function DeliveryCard({
  hasTrucks,
  receiverName,
  onReceiverNameChange,
  time,
  onTimeChange,
  deliveryDates,
  disabled = false,
}: DeliveryCardProps) {
  return (
    <StepCard title="Delivery">
      {hasTrucks ? (
        <Field label="Received by">
          <Input
            aria-label="Received by"
            value={receiverName}
            maxLength={80}
            placeholder="Full name"
            disabled={disabled}
            onChange={(event) => onReceiverNameChange(event.target.value)}
          />
        </Field>
      ) : null}
      <Field label="Time reading" helper={deliveryCoverageText(deliveryDates)}>
        <Input
          type="time"
          aria-label="Delivery time reading"
          value={time}
          disabled={disabled}
          className="md:max-w-[9rem]"
          onChange={(event) => onTimeChange(event.target.value)}
        />
      </Field>
    </StepCard>
  )
}

export interface ServiceCardProps {
  signature: string
  onSignatureChange: (next: string) => void
  time: string
  onTimeChange: (next: string) => void
  /** Set once the record is signed: the inputs lock and the card says who and when. */
  signed: { name: string | null; signedAt: string | null } | null
  disabled?: boolean
  /**
   * Amending a signed record: the reason renders above the signature and the
   * helper says both land on the amendment trail. The inputs stay open — the
   * record is being re-signed, not read back.
   */
  amendment?: { reason: string; onReasonChange: (next: string) => void } | null
}

export function ServiceCard({
  signature,
  onSignatureChange,
  time,
  onTimeChange,
  signed,
  disabled = false,
  amendment = null,
}: ServiceCardProps) {
  const locked = disabled || signed !== null
  return (
    <StepCard title="Service">
      {signed ? (
        signed.name ? (
          <SignedByLine name={signed.name} signedAt={signed.signedAt} />
        ) : (
          <p className="text-sm text-muted-foreground">Signed.</p>
        )
      ) : null}
      {amendment ? (
        <Field label="Reason for amendment">
          <Input
            aria-label="Reason for amendment"
            value={amendment.reason}
            maxLength={500}
            placeholder="Reason for amendment"
            disabled={locked}
            onChange={(event) => amendment.onReasonChange(event.target.value)}
          />
        </Field>
      ) : null}
      <Field
        label="Signature"
        helper={
          amendment
            ? 'Your name and reason will be recorded on the amendment.'
            : 'Your name will be recorded on the production record.'
        }>
        <Input
          aria-label="Signature"
          value={signature}
          placeholder="Type your full name to sign"
          autoComplete="name"
          disabled={locked}
          onChange={(event) => onSignatureChange(event.target.value)}
        />
      </Field>
      <Field label="Time reading">
        <Input
          type="time"
          aria-label="Service time reading"
          value={time}
          disabled={locked}
          className="md:max-w-[9rem]"
          onChange={(event) => onTimeChange(event.target.value)}
        />
      </Field>
    </StepCard>
  )
}
